import type {
  CartItem,
  DraftLineItem,
  FinancialSummary,
  Order,
  OrderLineItem,
} from "../types";

function getLineItemUnitPrice(
  order: Order,
  lineItemId: string,
  { includeTaxes = false }: { includeTaxes?: boolean } = {},
): number {
  const lineItem = order.lineItems?.find(
    (li: OrderLineItem) => li.line_item_id === lineItemId,
  );
  const price = parseFloat((lineItem as any)?.price ?? "0");
  if (isNaN(price)) return 0;

  let taxPerUnit = 0;
  if (includeTaxes) {
    // Use `unitTaxes` (the actual field name in LoadOrderWithItemsLineItemResponseDto).
    // Only add taxes that are NOT already included in `price` (HT shops: includedInPrice=false).
    // For TTC shops (includedInPrice=true) taxes are embedded in `price` — adding them again
    // would double-count.
    // unitTaxes.amount is the TOTAL tax for the whole line (all quantities), so divide by
    // quantity_bought to get the per-unit tax.
    const quantityBought: number =
      (lineItem as any)?.quantity_bought ?? (lineItem as any)?.quantity ?? 1;
    const unitTaxes: { amount?: number | string; includedInPrice?: boolean }[] =
      (lineItem as any)?.unitTaxes ?? [];
    const totalAdditionalTax = unitTaxes
      .filter((t) => !t.includedInPrice)
      .reduce((sum, t) => sum + parseFloat(String(t.amount ?? "0")), 0);
    taxPerUnit = quantityBought > 0 ? totalAdditionalTax / quantityBought : 0;
  }

  return (price + taxPerUnit) * 100;
}

/**
 * Returns 1 + effective tax rate for HT shops (taxesIncluded=false), or 1 for TTC shops.
 *
 * Used to convert rebuy cart items (always HT on Shopify storefronts for HT shops) to TTC,
 * so they can be compared on the same basis as the TTC exchange credit.
 *
 * Two strategies to derive the rate, in order:
 * 1. Sum unitTaxes[].rate (EU VAT shops that store an explicit rate).
 * 2. Derive rate from totalTax / unitPrice (US/CA Shopify shops that store rate=null but
 *    always provide amounts — e.g. Texas State Tax + Dallas City Tax bundled on one line).
 *    Mathematically equivalent to the stored rate; avoids the null-rate fallback that
 *    left taxMultiplier=1 for the exact scenario described in BBS-190 (CA 850$ HT → 960.5$
 *    TTC, phantom 110.5$ refund).
 *
 * Guard: only applies the multiplier when the unitTaxes amounts are non-zero.
 * Old DB records may have rate>0 but amount=0 — in that case getLineItemUnitPrice also added
 * 0, so keeping multiplier=1 preserves HT/HT consistency and avoids making things worse.
 */
export function getEffectiveTaxMultiplier(
  lineItems: DraftLineItem[],
  order: Order,
): number {
  for (const item of lineItems) {
    if (item.resolution !== "exchange-credit") continue;
    const li = order.lineItems?.find(
      (oli: OrderLineItem) => oli.line_item_id === item.lineItemId,
    );
    if (!li) continue;
    const unitTaxes: { rate?: number; amount?: number | string; includedInPrice?: boolean }[] =
      (li as any)?.unitTaxes ?? [];
    const additionalTaxes = unitTaxes.filter((t) => !t.includedInPrice);
    if (additionalTaxes.length === 0) return 1; // TTC shop — no conversion needed

    // Guard: only proceed when actual tax amounts are stored (amount > 0).
    // If amount=0 for all (old records), getLineItemUnitPrice also added 0 — keep HT/HT.
    const totalAdditionalTax = additionalTaxes.reduce(
      (sum, t) => sum + parseFloat(String(t.amount ?? "0")),
      0,
    );
    if (totalAdditionalTax === 0) return 1;

    // Strategy 1: use stored rates (EU VAT shops, rate is explicit).
    const additionalRate = additionalTaxes
      .filter((t) => (t.rate ?? 0) > 0)
      .reduce((sum, t) => sum + (t.rate ?? 0), 0);
    if (additionalRate > 0) return 1 + additionalRate;

    // Strategy 2: derive effective rate from amount / unit price.
    // Handles US/CA Shopify shops that split taxes into named components with rate=null
    // (e.g. "California State Tax", "Los Angeles County Tax", "Dallas City Tax").
    // totalAdditionalTax is for the whole line; divide by quantity_bought to get per-unit.
    const price = parseFloat(String((li as any)?.price ?? "0"));
    const quantityBought: number =
      (li as any)?.quantity_bought ?? (li as any)?.quantity ?? 1;
    const taxPerUnit = quantityBought > 0 ? totalAdditionalTax / quantityBought : 0;
    const effectiveRate = price > 0 ? taxPerUnit / price : 0;
    if (effectiveRate > 0) return 1 + effectiveRate;
  }
  return 1;
}

export function calculateTotals(
  lineItems: DraftLineItem[],
  order: Order,
  rebuyItems: CartItem[] = [],
): FinancialSummary {
  const currency = (order.currency as any)?.shopCurrency ?? "EUR";

  let refundTotal = 0;
  let exchangeVariantTotal = 0;
  let exchangeCreditTotal = 0;
  let discountTotal = 0;

  for (const item of lineItems) {
    const unitPrice = getLineItemUnitPrice(order, item.lineItemId);
    const itemTotal = unitPrice * item.quantity;

    switch (item.resolution) {
      case "refund":
        refundTotal += itemTotal;
        break;
      case "discount":
        discountTotal += itemTotal;
        break;
      case "exchange":
        exchangeVariantTotal += itemTotal;
        break;
      case "exchange-credit":
        // Compute TTC credit: for HT shops (unitTaxes.includedInPrice=false), add the
        // additional taxes so the credit reflects what the customer actually paid TTC.
        // For TTC shops (includedInPrice=true), getLineItemUnitPrice returns TTC as-is.
        exchangeCreditTotal +=
          getLineItemUnitPrice(order, item.lineItemId, { includeTaxes: true }) *
          item.quantity;
        break;
    }
  }

  // For HT shops, Shopify's /cart.js returns prices WITHOUT tax (HT).
  // Apply the same tax multiplier derived from the exchange-credit items so that
  // rebuyTotal is on a TTC basis and can be compared with the TTC exchangeCreditTotal.
  // For TTC shops the multiplier is 1 (no-op) and Shopify cart already returns TTC.
  const taxMultiplier = getEffectiveTaxMultiplier(lineItems, order);
  const rebuyTotal = rebuyItems.reduce(
    (sum, item) => sum + item.price * item.quantity * taxMultiplier,
    0,
  );
  const exchangeTotal = exchangeVariantTotal + exchangeCreditTotal;

  const amountDue = Math.max(
    0,
    rebuyTotal +
      exchangeVariantTotal -
      exchangeCreditTotal -
      refundTotal -
      discountTotal,
  );

  return {
    refundTotal,
    exchangeTotal,
    exchangeCreditTotal,
    discountTotal,
    rebuyTotal,
    amountDue,
    currency,
    taxMultiplier,
  };
}
